import { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useRecurly } from '@recurly/react-recurly'
import { Input } from '@pff-consumer/tweed-ui/atoms/input/input.web'
import { Button } from '@pff-consumer/tweed-ui/atoms/button/button'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/browser-text'
import { FormField } from '@pff-consumer/tweed-ui/molecules/form-field/form-field.web'
import { Dropdown } from '@pff-consumer/tweed-ui/molecules/dropdown/dropdown.web'
import { DEFAULT_OPTION_VALUES } from '@pff-consumer/schema'
import { subscribeFormSchema } from '@pff-consumer/utils/lib/accounts/subscribe-form-schema'
import { type SubscriptionInputFieldArgs, type SubscriptionFormInput } from '@pff-consumer/schema'
import { RecurlyCardElement } from '../recurly-card-element/recurly-card-element.web'
import { countries } from '../../../../shared/countries'
import { useStyles } from './credit-card-form.web.styles'
import type { CreditCardFormProps } from './credit-card-form.props'

const DEFAULT_COUNTRY_OPTION = { text: 'United States', value: 'US' }
const { useSelectedPlan } = checkoutHooks

export const CreditCardForm = ({
  loadingText,
  submitText,
  isFormOpen,
  isPaymentProcessing,
  isDisabled,
  isPending,
  onSubmit,
  onError,
  onComplete,
  enableAutoFocus = false,
}: CreditCardFormProps) => {
  const styles = useStyles({ isFormOpen })
  const recurly = useRecurly()
  const selectedPlan = useSelectedPlan()

  const [countryCode, setCountryCode] = useState<string>('US')
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitted, touchedFields },
  } = useForm<SubscriptionFormInput>({
    resolver: yupResolver(subscribeFormSchema),
    mode: 'onChange',
    shouldUseNativeValidation: true,
    defaultValues: {
      first_name: '',
      last_name: '',
      address1: '',
      city: '',
      state: '',
      country: DEFAULT_COUNTRY_OPTION.value,
      postal_code: '',
      cardDetails: {},
    },
  })

  const tabIndex = isFormOpen ? 0 : -1

  const formRef = useRef<HTMLFormElement>(null)

  const processPayment: SubmitHandler<SubscriptionFormInput> = useCallback(() => {
    onSubmit?.()

    recurly.token(formRef.current!, (error, token: { id: string }) => {
      if (error) {
        onError(error)
      } else if (token) {
        onComplete(token.id, 'credit_card', selectedPlan)
      }
    })
  }, [selectedPlan, onComplete, onError, onSubmit, recurly])

  const handleFormSubmit = useCallback(() => {
    handleSubmit(processPayment)()
  }, [handleSubmit, processPayment])

  const country = countries.find(({ countryShortCode }) => countryShortCode === countryCode)
  const regions = country ? country.regions : []

  const renderInputField = (args: SubscriptionInputFieldArgs) => {
    const { name, label, disabled = false, type = 'text', maxLength } = args
    const isError = !!errors[name]?.message

    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, onBlur } }) => (
          <FormField errorMessage={errors[name]?.message}>
            <Input
              dataRecurly={name}
              id={label}
              type={type}
              placeholder={label}
              value={value as string}
              onChange={onChange}
              disabled={disabled}
              isError={isError}
              onBlur={onBlur}
              maxLength={maxLength}
            />
          </FormField>
        )}
      />
    )
  }

  const handleCountryChange = (value: string, onChange: (...event: any[]) => void) => {
    onChange(value)
    setCountryCode(value)
    setValue('state', '')
  }

  useEffect(() => {
    if (isFormOpen && enableAutoFocus) {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [isFormOpen, enableAutoFocus])

  return (
    <form
      ref={formRef}
      className={styles.container}
      autoComplete='on'
    >
      <Text variant={TextDescriptionVariant.REGULAR_PRIMARY_REGULAR}>
        Card Details<span className={styles.mandatoryIndicator}>*</span>
      </Text>
      <div className={styles.fieldsContainer}>
        {renderInputField({ name: 'first_name', label: 'First Name', maxLength: 50 })}
        {renderInputField({ name: 'last_name', label: 'Last Name', maxLength: 50 })}
      </div>

      <Controller
        control={control}
        name='cardDetails'
        render={({ field: { onChange, onBlur } }) => (
          <FormField errorMessage={touchedFields.cardDetails || isSubmitted ? errors.cardDetails?.message : ''}>
            <RecurlyCardElement
              tabIndex={`${tabIndex}`}
              onChange={onChange}
              onBlur={onBlur}
            />
          </FormField>
        )}
      />

      {renderInputField({ name: 'address1', label: 'Address' })}
      {renderInputField({ name: 'city', label: 'City' })}

      <div className={styles.fieldsContainer}>
        <Controller
          control={control}
          name='state'
          render={({ field: { onChange, value } }) => {
            const options = regions.map(({ shortCode, name }) => ({
              text: name,
              value: shortCode || name,
            }))

            const selectedOption = options.find((option) => option.value === value)

            const defaultOption = selectedOption ?? DEFAULT_OPTION_VALUES

            return (
              <FormField errorMessage={errors.state?.message}>
                <Dropdown
                  id='state'
                  dataRecurly='state'
                  placeholder='State'
                  defaultOption={defaultOption}
                  options={options}
                  onSelectOption={(value) => onChange(value.value)}
                  isError={!!errors.state?.message}
                  containerStyles={styles.dropdowns}
                />
              </FormField>
            )
          }}
        />
        <Controller
          control={control}
          name='country'
          render={({ field: { onChange } }) => (
            <FormField errorMessage={errors.country?.message}>
              <Dropdown
                id='country'
                dataRecurly='country'
                defaultOption={DEFAULT_COUNTRY_OPTION}
                options={countries.map(({ countryShortCode, countryName }) => ({
                  text: countryName,
                  value: countryShortCode,
                }))}
                onSelectOption={(value) => handleCountryChange(value.value, onChange)}
                isError={!!errors.country?.message}
                containerStyles={styles.dropdowns}
              />
            </FormField>
          )}
        />
      </div>

      {renderInputField({ name: 'postal_code', label: 'Zip Code' })}

      <div className={styles.button}>
        <Button
          size='lg'
          accessibilityLabel='Subscribe Button'
          onPress={handleFormSubmit}
          isLoading={isPending && isPaymentProcessing}
          disabled={isDisabled || isPending}
          loadingText={loadingText}
          title={submitText}
          data-analytics-id='credit-card-form-submit'
        />
      </div>
    </form>
  )
}
